// The contact tab strip (App.jsx's TabStrip, 2026-10-21; docs/contact-view.md
// "Contact tabs"). Pure helpers, unit-tested in ui/test/contactTabs.test.js.
// Every value comes from what the app already loads for each open contact:
// the conversation detail its pane fetched, the Queue row when the contact is
// on the page, and the pane's own assignment heartbeat. Nothing here makes a
// request; the timer re-renders from these values every second.
import { partyName } from './partyName.js'
import { elapsedOnContact, formatElapsed } from './contactTimer.js'
import { latestPark } from './parking.js'

// "2 contacts open".
export function tabsOpenLabel(count) {
  const n = Number(count) || 0
  return `${n} contact${n === 1 ? '' : 's'} open`
}

// "#8028793", or the start of the internal id while the number is not known
// yet (the detail has not loaded, or the viewer may no longer open it and no
// Queue row has it).
export function tabNumber({ convNumber = null } = {}, id = null) {
  if (convNumber != null && convNumber !== '') return `#${convNumber}`
  return String(id ?? '').slice(0, 8)
}

// The customer exactly as the Queue row shows them (partyName(), the same
// helper the conversation list uses): from the Queue row when the contact is
// on the page (it carries customerEmail), else from the contact's own
// detail. Null when neither is loaded.
export function tabCustomer(row, detail) {
  const source = row ?? detail ?? null
  if (!source) return null
  return partyName(source)
}

// The tab's timer, the same value and state as the "time on this contact"
// chip in the contact's header (ActiveContactTimer.jsx):
//   running  the viewer holds the claim on an open contact: elapsed time on
//            the claim minus parked time, ticking
//   paused   the same, while the claim waits on the "Are you still working
//            on this contact?" check-in
//   parked   the contact is parked (an open park): "Parked"
//   null     no timer (not the viewer's claim, or not open), as the header
//            shows none
// { state, text } or null.
export function tabTimer({ status = null, assignment = null, parks = null } = {}, now = Date.now()) {
  const latest = latestPark(parks)
  if (assignment?.park || (latest && !latest.unparkedAt)) return { state: 'parked', text: 'Parked' }
  const assignedAt = assignment?.owner?.assignedAt
  if (status !== 'open' || !assignment?.isOwner || assignedAt == null) return null
  const text = formatElapsed(elapsedOnContact(now, assignedAt, assignment.owner?.parkedMs ?? 0))
  return assignment.inactivityPrompt ? { state: 'paused', text } : { state: 'running', text }
}

// The latest customer message on a contact's detail, for the new-message dot.
export function latestInboundId(detail) {
  const inbound = (detail?.messages ?? []).filter((m) => m.direction === 'inbound')
  return inbound.length ? (inbound[inbound.length - 1].id ?? null) : null
}

// The new-message dot. `seen` maps a tab's id to the latest customer message
// the viewer has had on screen. A tab's first value, and every value while
// it is the active tab, counts as seen; a background tab whose latest
// customer message is a newer one gets the dot until it is opened.
export function nextSeenInbound(seen, id, lastInboundId, isActive) {
  if (lastInboundId == null) return seen
  if (isActive || !(id in (seen ?? {}))) {
    if (seen?.[id] === lastInboundId) return seen
    return { ...(seen ?? {}), [id]: lastInboundId }
  }
  return seen
}

export function hasNewMessage(seen, id, lastInboundId, isActive) {
  if (isActive || lastInboundId == null) return false
  return id in (seen ?? {}) && seen[id] !== lastInboundId
}

// A pick from a Queue row's "related ▾" dropdown (2026-10-22). With a
// contact open it behaves like the related banners: the pick opens in a
// contact tab and the open contact stays open. `openIds` is every contact
// open now (the primary plus the tab strip):
//   'select'  nothing is open: open it the normal way, as a row click does,
//             so the first contact never lands in a tab strip of one
//   'switch'  it is already open: switch to its tab, never a second copy
//   'tab'     open it in a new tab and switch to it
export function queueRelatedOpenMode(openIds, targetId) {
  const ids = (openIds ?? []).filter((v) => v != null)
  if (ids.length === 0) return 'select'
  return ids.includes(targetId) ? 'switch' : 'tab'
}
